import React, { useEffect, useRef, useState } from 'react'
import { API_ENDPOINTS } from '../config/api'
import { Navigate } from 'react-router-dom'
import { useI18n } from '../i18n'
import { USE_MOCK_API } from '../mocks/useMockApi'
import { useAuth } from '../components/AuthProvider'
import { Button } from '../components/ui/Button'
import './Login.css'

function formatRetryAfter(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return ''
  if (seconds < 60) return `${Math.ceil(seconds)}s`
  const m = Math.ceil(seconds / 60)
  return `${m} min`
}

const Login: React.FC = () => {
  const { t } = useI18n()
  const { isAuthenticated, initialized } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [retryAfterSec, setRetryAfterSec] = useState<number | null>(null)
  const inFlightRef = useRef(false)

  useEffect(() => {
    if (USE_MOCK_API) return
    const prev = document.body.style.backgroundColor
    document.body.style.backgroundColor = '#003a6a'
    return () => {
      document.body.style.backgroundColor = prev
    }
  }, [])

  useEffect(() => {
    if (retryAfterSec == null || retryAfterSec <= 0) return undefined
    const id = window.setTimeout(() => {
      setRetryAfterSec((prev) => (prev == null || prev <= 1 ? null : prev - 1))
    }, 1000)
    return () => window.clearTimeout(id)
  }, [retryAfterSec])

  if (USE_MOCK_API) {
    return <Navigate to="/home" replace />
  }

  if (!initialized) {
    return null
  }

  if (isAuthenticated) {
    return <Navigate to="/home" replace />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // No automatic retries; block double-submit while in flight or rate-limited
    if (inFlightRef.current || submitting) return
    if (retryAfterSec != null && retryAfterSec > 0) {
      setError(
        t(
          'auth.rateLimitedWait',
          `Too many login attempts. Please wait ${formatRetryAfter(retryAfterSec)}.`,
        ).replace('{time}', formatRetryAfter(retryAfterSec)),
      )
      return
    }

    inFlightRef.current = true
    setSubmitting(true)
    setError('')

    try {
      const response = await fetch(API_ENDPOINTS.LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      })

      const retryHeader = response.headers.get('Retry-After')
      const data = await response.json().catch(() => ({}))

      if (response.status === 429 || data?.code === 'AUTH_RATE_LIMITED') {
        const wait =
          Number(data?.retryAfter) ||
          (retryHeader ? Number(retryHeader) : NaN) ||
          60
        const sec = Number.isFinite(wait) && wait > 0 ? Math.ceil(wait) : 60
        setRetryAfterSec(sec)
        setError(
          t(
            'auth.rateLimited',
            `Too many login attempts. Please try again in ${formatRetryAfter(sec)}.`,
          ).replace('{time}', formatRetryAfter(sec)),
        )
        return
      }

      if (response.ok && data?.token) {
        localStorage.setItem('token', data.token)
        localStorage.setItem('user', JSON.stringify(data.user))
        window.location.href = '/home'
        return
      }

      setError(t('auth.invalidCredentials', 'Invalid username or password. Please try again.'))
    } catch {
      setError(t('auth.loginFailed', 'Login failed. Please try again.'))
    } finally {
      inFlightRef.current = false
      setSubmitting(false)
    }
  }

  const blocked = submitting || (retryAfterSec != null && retryAfterSec > 0)

  return (
    <div id="login-page" className="login-page">
      <div className="login-card">
        <div className="text-center">
          <h1 className="login-title">DefendSphere</h1>
        </div>

        <form id="login-form" onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="username" className="login-label">
              Username
            </label>
            <input
              type="text"
              id="username"
              name="username"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="login-input"
              autoComplete="username"
              disabled={submitting}
            />
          </div>

          <div className="mb-4">
            <label htmlFor="password" className="login-label">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="login-input"
              autoComplete="current-password"
              disabled={submitting}
            />
          </div>

          {error && (
            <div className="login-error text-sm text-center mb-2" role="alert">
              {error}
            </div>
          )}

          <Button
            type="submit"
            color="primary"
            fullWidth
            className="login-button mt-2 mb-4"
            disabled={blocked}
          >
            {submitting
              ? t('auth.signingIn', 'Signing in…')
              : retryAfterSec != null && retryAfterSec > 0
                ? t('auth.waitToRetry', `Wait ${formatRetryAfter(retryAfterSec)}`).replace(
                    '{time}',
                    formatRetryAfter(retryAfterSec),
                  )
                : t('auth.login', 'Sign in')}
          </Button>

          <div className="text-center">
            <a href="#" className="text-sm login-link">
              Forgot your password?
            </a>
          </div>
          <div className="text-center mt-2">
            <p className="text-sm login-secondary">
              Don’t have an account? <a href="#" className="login-link">Sign up</a>
            </p>
          </div>
        </form>
      </div>
    </div>
  )
}

export default Login
